import { supabase } from '../../../lib/supabase';

export interface TranslationContribution {
  id: string;
  questionId: string;
  language: string;
  fieldName: 'question' | 'options' | 'explanation' | 'correct_answer';
  originalText: string;
  suggestedText: string;
  contributorId: string;
  votesUp: number;
  votesDown: number;
  status: 'pending' | 'approved' | 'rejected';
  moderatorId?: string;
  moderatorNotes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface TranslationQualityMetrics {
  language: string;
  contentType: 'question' | 'category' | 'ui';
  totalItems: number;
  reviewedItems: number;
  approvedItems: number;
  avgQualityScore: number;
  completionPercentage: number;
  lastUpdated: Date;
}

export interface QualityReview {
  translationId: string;
  reviewerId: string;
  qualityScore: number; // 1-5 scale
  feedback: string;
  culturalAdaptation: number; // 1-5 scale
  biblicalAccuracy: number; // 1-5 scale
  linguisticQuality: number; // 1-5 scale
  approved: boolean;
}

export class TranslationQualityService {
  // Submit a translation contribution
  async submitTranslationContribution(contribution: Omit<TranslationContribution, 'id' | 'votesUp' | 'votesDown' | 'status' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const { data, error } = await supabase
        .from('translation_contributions')
        .insert({
          question_id: contribution.questionId,
          language: contribution.language,
          field_name: contribution.fieldName,
          original_text: contribution.originalText,
          suggested_text: contribution.suggestedText,
          contributor_id: contribution.contributorId
        })
        .select('id')
        .single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      console.error('Failed to submit translation contribution:', error);
      throw error;
    }
  }

  // Vote on a translation contribution
  async voteOnContribution(contributionId: string, userId: string, voteType: 'up' | 'down'): Promise<void> {
    void contributionId;
    void userId;
    void voteType;
    throw new Error('Community translation voting is not available in this release.');
  }

  // Get pending contributions for moderation
  async getPendingContributions(language?: string, limit: number = 50): Promise<TranslationContribution[]> {
    try {
      let query = supabase
        .from('translation_contributions')
        .select(`
          id,
          question_id,
          language,
          field_name,
          original_text,
          suggested_text,
          contributor_id,
          votes_up,
          votes_down,
          status,
          moderator_id,
          moderator_notes,
          created_at,
          updated_at
        `)
        .eq('status', 'pending')
        .order('votes_up', { ascending: false })
        .limit(limit);

      if (language) {
        query = query.eq('language', language);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(this.mapContributionFromDB) || [];
    } catch (error) {
      console.error('Failed to get pending contributions:', error);
      throw error;
    }
  }

  // Approve a translation contribution
  async approveContribution(contributionId: string, moderatorId: string, notes?: string): Promise<void> {
    void contributionId;
    void moderatorId;
    void notes;
    throw new Error('Community translation moderation is not available in this release.');
  }

  // Reject a translation contribution
  async rejectContribution(contributionId: string, moderatorId: string, reason: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('translation_contributions')
        .update({
          status: 'rejected',
          moderator_id: moderatorId,
          moderator_notes: reason,
          updated_at: new Date().toISOString()
        })
        .eq('id', contributionId);

      if (error) throw error;
    } catch (error) {
      console.error('Failed to reject contribution:', error);
      throw error;
    }
  }

  // Submit a quality review for a translation
  async submitQualityReview(review: QualityReview): Promise<void> {
    try {
      // Calculate overall quality score
      const overallScore = (review.culturalAdaptation + review.biblicalAccuracy + review.linguisticQuality) / 3;

      // Update the translation with quality score
      const { error: updateError } = await supabase
        .from('question_translations')
        .update({
          quality_score: overallScore,
          review_status: review.approved ? 'approved' : 'needs_review',
          reviewed_by: review.reviewerId,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', review.translationId);

      if (updateError) throw updateError;

      // Store detailed review (if we had a reviews table)
      // This would store the detailed feedback and scores

      // Update quality metrics
      await this.updateQualityMetrics();
    } catch (error) {
      console.error('Failed to submit quality review:', error);
      throw error;
    }
  }

  // Get translation quality metrics for a language
  async getQualityMetrics(language: string): Promise<TranslationQualityMetrics[]> {
    try {
      const { data, error } = await supabase
        .from('translation_quality_metrics')
        .select('*')
        .eq('language', language);

      if (error) throw error;

      return data?.map(item => ({
        language: item.language,
        contentType: item.content_type,
        totalItems: item.total_items,
        reviewedItems: item.reviewed_items,
        approvedItems: item.approved_items,
        avgQualityScore: item.avg_quality_score,
        completionPercentage: item.completion_percentage,
        lastUpdated: new Date(item.last_updated)
      })) || [];
    } catch (error) {
      console.error('Failed to get quality metrics:', error);
      throw error;
    }
  }

  // Get all language quality overview
  async getAllLanguageQuality(): Promise<Record<string, TranslationQualityMetrics[]>> {
    try {
      const { data, error } = await supabase
        .from('translation_quality_metrics')
        .select('*')
        .order('language');

      if (error) throw error;

      const result: Record<string, TranslationQualityMetrics[]> = {};
      
      data?.forEach(item => {
        if (!result[item.language]) {
          result[item.language] = [];
        }
        result[item.language].push({
          language: item.language,
          contentType: item.content_type,
          totalItems: item.total_items,
          reviewedItems: item.reviewed_items,
          approvedItems: item.approved_items,
          avgQualityScore: item.avg_quality_score,
          completionPercentage: item.completion_percentage,
          lastUpdated: new Date(item.last_updated)
        });
      });

      return result;
    } catch (error) {
      console.error('Failed to get all language quality:', error);
      throw error;
    }
  }

  // Update quality metrics (called after reviews/approvals)
  async updateQualityMetrics(): Promise<void> {
    return;
  }

  // Get translations needing review for a language
  async getTranslationsNeedingReview(language: string, limit: number = 20): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('question_translations')
        .select(`
          id,
          question_id,
          language,
          question,
          options,
          correct_answer,
          explanation,
          quality_score,
          review_status,
          created_at
        `)
        .eq('language', language)
        .eq('review_status', 'pending')
        .order('created_at', { ascending: true })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to get translations needing review:', error);
      throw error;
    }
  }

  // Bulk approve translations (for professional translator batches)
  async bulkApproveTranslations(translationIds: string[], reviewerId: string, qualityScore: number = 4.5): Promise<void> {
    try {
      const { error } = await supabase
        .from('question_translations')
        .update({
          quality_score: qualityScore,
          review_status: 'approved',
          reviewed_by: reviewerId,
          reviewed_at: new Date().toISOString()
        })
        .in('id', translationIds);

      if (error) throw error;

      // Update quality metrics
      await this.updateQualityMetrics();
    } catch (error) {
      console.error('Failed to bulk approve translations:', error);
      throw error;
    }
  }

  // Get contributor leaderboard
  async getContributorLeaderboard(language?: string, timeframe: 'week' | 'month' | 'all' = 'month'): Promise<any[]> {
    try {
      let query = supabase
        .from('translation_contributions')
        .select(`
          contributor_id,
          user_profiles!inner(display_name, avatar_url)
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (language) {
        query = query.eq('language', language);
      }

      if (timeframe !== 'all') {
        const days = timeframe === 'week' ? 7 : 30;
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);
        query = query.gte('created_at', cutoffDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Failed to get contributor leaderboard:', error);
      throw error;
    }
  }

  // Get translation progress for a language
  async getTranslationProgress(language: string): Promise<{
    totalQuestions: number;
    translatedQuestions: number;
    reviewedQuestions: number;
    approvedQuestions: number;
    progressPercentage: number;
    qualityScore: number;
  }> {
    try {
      // Get total questions
      const { count: totalQuestions } = await supabase
        .from('questions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      // Get translation stats
      const { data: translationStats } = await supabase
        .from('question_translations')
        .select('review_status, quality_score')
        .eq('language', language);

      const translatedQuestions = translationStats?.length || 0;
      const reviewedQuestions = translationStats?.filter(t => t.review_status !== 'pending').length || 0;
      const approvedQuestions = translationStats?.filter(t => t.review_status === 'approved').length || 0;
      
      const qualityScores = translationStats?.filter(t => t.quality_score > 0).map(t => t.quality_score) || [];
      const avgQualityScore = qualityScores.length > 0 
        ? qualityScores.reduce((sum, score) => sum + score, 0) / qualityScores.length 
        : 0;

      return {
        totalQuestions: totalQuestions || 0,
        translatedQuestions,
        reviewedQuestions,
        approvedQuestions,
        progressPercentage: totalQuestions ? (approvedQuestions / totalQuestions) * 100 : 0,
        qualityScore: avgQualityScore
      };
    } catch (error) {
      console.error('Failed to get translation progress:', error);
      throw error;
    }
  }

  // Private helper methods
  private mapContributionFromDB(dbItem: any): TranslationContribution {
    return {
      id: dbItem.id,
      questionId: dbItem.question_id,
      language: dbItem.language,
      fieldName: dbItem.field_name,
      originalText: dbItem.original_text,
      suggestedText: dbItem.suggested_text,
      contributorId: dbItem.contributor_id,
      votesUp: dbItem.votes_up,
      votesDown: dbItem.votes_down,
      status: dbItem.status,
      moderatorId: dbItem.moderator_id,
      moderatorNotes: dbItem.moderator_notes,
      createdAt: new Date(dbItem.created_at),
      updatedAt: new Date(dbItem.updated_at)
    };
  }
}

// Singleton instance
export const translationQualityService = new TranslationQualityService();
